import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { Workspace } from '@app/modules/workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '@app/modules/workspaces/services/workspace.service'
import { environment } from '@environments/environment'
import { Language } from '@personas/interfaces/persona-options.interface'
import { forkJoin, Observable, of, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'

const defaultTranslations = {
  1: {
    ara: 'نص عادي',
    eng: 'Plain text',
    afr: 'Gewone teks',
    est: 'Vroulik',
    fra: 'Texte brut',
    por: '',
    rus: 'Простой текст',
    ser: 'Običan tekst ',
    esp: '',
    fil: 'Payak na teksto',
    urd: 'خالی ٹیکسٹ'
  },
  3: {
    ara: 'البريد الإلكتروني',
    eng: 'E-mail',
    afr: 'E-pos',
    est: 'E-mail',
    fra: 'E-mail',
    por: '',
    rus: 'Электронная почта',
    ser: 'Email',
    esp: '',
    fil: 'E-mail',
    urd: 'ایمیل'
  },
  4: {
    ara: 'رقم الهاتف',
    eng: 'Phone number',
    afr: 'Tellefoon nommer',
    est: 'Telefoninumber',
    fra: 'Numéro de téléphone',
    por: '',
    rus: 'Номер телефона',
    ser: 'Broj telefona',
    esp: '',
    fil: 'Numero ng telepono',
    urd: 'فون نمبر'
  },
  5: {
    ara: 'الاسم بالكامل',
    eng: 'Full name',
    afr: 'Volle naam en van',
    est: 'Ees- ja perekonnanimi',
    fra: 'Nom et prénom',
    por: '',
    rus: 'Полное имя',
    ser: 'Ime i prezime',
    esp: '',
    fil: 'Buong pangalan',
    urd: 'پورا نام'
  },
  7: {
    ara: 'بلد الإقامة',
    eng: 'Country',
    afr: 'Land / Land van herkoms',
    est: 'Riik',
    fra: 'Pays',
    por: '',
    rus: 'Страна',
    ser: 'Država',
    esp: '',
    fil: 'Bansa',
    urd: 'ملک'
  },
  9: {
    ara: 'رفع ملف',
    eng: 'Upload field',
    afr: 'Oplaai veld',
    est: 'Üleslaadimisväli',
    fra: 'Champ de téléchargement',
    por: '',
    rus: 'Поле для загрузки',
    ser: 'Dodaj datoteku',
    esp: '',
    fil: 'Lugar na pwede mg upload',
    urd: 'اپ لوڈ فیلڈ'
  },
  10: {
    ara: 'موقع الكتروني',
    eng: 'Website',
    afr: 'Webtuiste',
    est: 'Veebileht',
    fra: 'Site Internet',
    por: '',
    rus: 'Веб-сайт',
    ser: 'Intrnet stranica ',
    esp: '',
    fil: 'Website',
    urd: 'ویب سائٹ'
  },
  12: {
    ara: 'اختيار متعدد',
    eng: 'Multiple select',
    afr: 'Multi keuse',
    est: 'Mitu valikut',
    fra: 'Sélection multiple',
    por: '',
    rus: 'Множественный выбор',
    ser: 'Mogućnost odabira više opcija',
    esp: '',
    fil: 'Maramihang pili',
    urd: 'ملٹیپل سلیکٹ'
  },
  14: {
    ara: 'اختيار واحد',
    eng: 'Single select',
    afr: 'Enkel keuse',
    est: 'Üksikvalik',
    fra: 'Sélection unique',
    por: '',
    rus: 'Одиночный выбор',
    ser: 'Odabrati jednu opciju',
    esp: '',
    fil: 'Isahang pili',
    urd: 'سنگل سلیکٹ'
  },
  15: {
    ara: 'تاريخ',
    eng: 'Date',
    afr: 'Datum',
    est: 'Kuupäev',
    fra: 'Date',
    por: '',
    rus: 'Дата',
    ser: 'Datum',
    esp: '',
    fil: 'Petsa',
    urd: 'تاریخ'
  },
  22: {
    ara: 'الفئة العمرية',
    eng: 'Age group',
    afr: 'Ouderdoms groep',
    est: 'Vanusegrupp',
    fra: "Tranche d'âge",
    por: '',
    rus: 'Возрастная группа',
    ser: 'Starosna grupa',
    esp: '',
    fil: 'Pangkat ng edad',
    urd: 'عمر گروپ'
  },
  24: {
    ara: 'النوع',
    eng: 'Gender',
    afr: 'Geslag',
    est: 'Sugu',
    fra: 'Genre',
    por: '',
    rus: 'Пол',
    ser: 'Rod',
    esp: '',
    fil: 'Kasarian',
    urd: 'جنس'
  },
  32: {
    ara: 'المنظمة',
    eng: 'Organization',
    afr: 'Organisasie',
    est: 'Organisatsioon',
    fra: 'Entreprise',
    por: '',
    rus: 'Организация',
    ser: 'Organizacija',
    esp: '',
    fil: 'Organisasyon',
    urd: 'ملازمت'
  },
  33: {
    ara: 'المسمى الوظيفي',
    eng: 'Job title',
    afr: 'Werks Titel',
    est: 'Ametinimetus',
    fra: "Titre d'emploi",
    por: '',
    rus: 'Должность',
    ser: 'Zanimanje',
    esp: '',
    fil: 'Titulo sa trabaho',
    urd: 'ملازمت کا عنوان'
  },
  36: {
    ara: 'حقل رقمي',
    eng: 'Numeric',
    afr: 'Numeries',
    est: 'Numbriline',
    fra: 'Champ numérique',
    por: '',
    rus: 'Числовой',
    ser: 'Numerički',
    esp: '',
    fil: 'Numero',
    urd: 'عدد'
  },
  40: {
    ara: 'رسالة توضيحية',
    eng: 'Info Message',
    afr: 'Informasie Boodskap',
    est: 'Infoteade',
    fra: "Message d'information",
    por: '',
    rus: 'Информационное сообщение',
    ser: 'Obaveštajna poruka',
    esp: '',
    fil: 'Mensahe ng Impormasyon',
    urd: 'معلوماتی پیغام'
  },
  41: {
    ara: 'رسالة تحذيرية',
    eng: 'Warning Message',
    afr: 'Waaskuwings Boodskap',
    est: 'Hoiatusteade',
    fra: "Message d'alerte",
    por: '',
    rus: 'Предупреждающее сообщение',
    ser: 'Poruka upozorenja',
    esp: '',
    fil: 'Babalang mensahe',
    urd: 'انتباہی پیغام'
  },
  43: {
    ara: 'رقم البطاقة الشخصية/ رقم جواز السفر',
    eng: 'ID/Passport No.',
    afr: '',
    est: '',
    fra: '',
    por: '',
    rus: '',
    ser: '',
    esp: '',
    fil: '',
    urd: ''
  },
  44: {
    ara: 'مدينة الإقامة',
    eng: 'City of residence',
    afr: '',
    est: '',
    fra: '',
    por: '',
    rus: '',
    ser: '',
    esp: '',
    fil: '',
    urd: ''
  }
}

const defaultValueTranslations = {
  male: {
    ara: 'ذكر',
    eng: 'Male',
    afr: 'Manlik',
    est: 'Mees',
    fra: 'Homme',
    por: '',
    rus: 'Мужской',
    ser: 'Muški',
    esp: '',
    fil: 'Lalaki',
    urd: 'مرد'
  },
  female: {
    ara: 'أنثى',
    eng: 'Female',
    afr: 'Vroulik',
    est: 'Naine',
    fra: 'Femme',
    por: '',
    rus: 'Женский',
    ser: 'Ženski',
    esp: '',
    fil: 'Babae',
    urd: 'عورت'
  },
  '<24': {
    ara: '<24',
    eng: '<24',
    afr: '<24',
    est: '<24',
    fra: '<24',
    por: '<24',
    rus: '<24',
    ser: '<24',
    esp: '<24',
    fil: '<24',
    urd: '<24'
  },
  '24-45': {
    ara: '24-45',
    eng: '24-45',
    afr: '24-45',
    est: '24-45',
    fra: '24-45',
    por: '24-45',
    rus: '24-45',
    ser: '24-45',
    esp: '24-45',
    fil: '24-45',
    urd: '24-45'
  },
  '46-65': {
    ara: '46-65',
    eng: '46-65',
    afr: '46-65',
    est: '46-65',
    fra: '46-65',
    por: '46-65',
    rus: '46-65',
    ser: '46-65',
    esp: '46-65',
    fil: '46-65',
    urd: '46-65'
  },
  '>65': {
    ara: '>65',
    eng: '>65',
    afr: '>65',
    est: '>65',
    fra: '>65',
    por: '>65',
    rus: '>65',
    ser: '>65',
    esp: '>65',
    fil: '>65',
    urd: '>65'
  }
}
@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  addNewFormFieldEvent$ = new Subject()
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   * @param auth AuthService
   */
  constructor(private http: HttpClient, private auth: AuthService, private ws: WorkspaceService) {}

  availableWorkspaceLanguages: Language[] = []
  /**
   * Retrieves all languages for the current workspace
   * @returns Observable<Language[]>
   */
  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${environment.planningApi}language?workspace=${this.auth.currentWorkspace.slug}`).pipe(
      tap((langs) => {
        this.availableWorkspaceLanguages = langs
      })
    )
  }

  /**
   * Create a new language
   * @param language Language
   * @returns Observable<[Workspace, Language]>
   */
  createLanguage(language: Language): Observable<[Workspace, Language]> {
    // Generate a slug for the new language
    const slug = this.generateSlug(language.name.slice(0, 3), this.availableWorkspaceLanguages.length, this.availableWorkspaceLanguages)
    language['slug'] = slug
    const languages: Language[] = this.auth.currentWorkspace.options?.languages ?? []
    languages.push(language)
    const workspaceOptions = { ...this.auth.currentWorkspace.options, languages }
    const updateWorkspaceRequest = this.ws.updateWorkspace({ id: this.auth.currentWorkspace.id, options: workspaceOptions }).pipe(
      tap((workspace: Workspace) => {
        this.auth.currentWorkspace = workspace as Workspace
        return workspace
      })
    )
    return forkJoin([updateWorkspaceRequest, of({ ...language, slug: slug })])
  }

  /**
   * Delete a language
   * @param languageSlug string
   * @returns Observable<any>
   */
  deleteLanguage(languageSlug: string): Observable<any> {
    return this.http
      .delete(`${environment.planningApi}language`, {
        params: {
          workspace: this.auth.currentWorkspace.slug,
          slug: languageSlug
        }
      })
      .pipe(
        tap(() => {
          delete this.auth.currentWorkspace.options.languages[languageSlug]
          this.availableWorkspaceLanguages = this.availableWorkspaceLanguages.filter((lang) => lang.slug !== languageSlug)
        })
      )
  }

  /**
   * Generates a suitable language slug based on multiple factors and iterations
   * @param slug string
   * @param index number
   * @param elements Language[]
   * @param iteration 0
   * @returns string
   */
  private generateSlug(slug: string, index: number, elements: Language[], iteration = 0): string {
    slug = slug
      .replace(' ', '')
      .replace(
        /[^a-zA-Z0-9]/g,
        Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
      )
      .toLowerCase()
    return elements.some((o, elementIndex) => o.slug === slug && index !== elementIndex)
      ? this.generateSlug(slug.replace((iteration - 1).toString(), '') + iteration.toString(), index, elements, iteration + 1).toLowerCase()
      : slug.toLowerCase()
  }

  /**
   * Load the default translation for the given form element type
   * @param form_element_type_id
   * @param languageSlug
   * @returns string
   */
  loadDefaultTranslation(form_element_type_id: any, languageSlug: string): string {
    const hasLable = Object.prototype.hasOwnProperty.call(defaultTranslations, form_element_type_id)
    return hasLable ? defaultTranslations[form_element_type_id][languageSlug] || defaultTranslations[form_element_type_id]['eng'] : ''
  }

  /**
   * Load the default translation for the given form element value slug
   * @param valueSlug
   * @param languageSlug
   * @returns
   */
  loadDefaultValueTranslations(valueSlug: string, languageSlug: string): string {
    return defaultValueTranslations[valueSlug.toLocaleLowerCase()]
      ? defaultValueTranslations[valueSlug.toLocaleLowerCase()][languageSlug] || defaultValueTranslations[valueSlug.toLocaleLowerCase()]['eng']
      : ''
  }

  /**
   * Gets the workspace language list
   * @returns Language[]
   */
  getWorkspaceLanguages(): Language[] {
    return this.auth.currentWorkspace.options?.languages ? this.auth.currentWorkspace.options.languages : []
  }
}
